import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import {
    useGetDepositPriceListQuery,
    usePostDepositPriceMutation,
    usePutDepositPriceMutation,
    useDeleteDepositPriceMutation,
} from "@/redux/contractApi";
import { dateFormat, dateFormatDateTime, comma, uncomma } from "@/utils";
import { InputPopup, Confirm } from "@/components";
import DatePicker from "react-datepicker";
import ko from "date-fns/locale/ko";

export const DepositHistory = ({ formData, setFormData, contractGroupId }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [confirmStatus, setConfirmStatus] = useState({
        confirmPopupStatus: false,
        confirmTitle: "",
        confirmDescription: "",
        confirmButtons: [],
    });

    const [postDepositPrice, { isLoading: isSaving }] = usePostDepositPriceMutation();
    const [putDepositPrice, { isLoading: isUpdating }] = usePutDepositPriceMutation();
    const [deleteDepositPrice, { isLoading: isDeleting }] = useDeleteDepositPriceMutation();
    const { data: depositListData, refetch } = useGetDepositPriceListQuery(contractGroupId, {
        skip: !contractGroupId,
    });

    const handleAddDeposit = () => {
        setEditingItem(null);
        setIsModalOpen(true);
    };

    const handleEditDeposit = (item) => {
        console.log("수정할 아이템:", item); // 디버깅용
        setEditingItem(item);
        setIsModalOpen(true);
    };

    const handleDeleteDeposit = (chargeDepositId) => {
        setConfirmStatus({
            confirmPopupStatus: true,
            confirmTitle: "입금 내역 삭제",
            confirmDescription: "정말 삭제하시겠습니까?",
            confirmButtons: [
                {
                    label: "예",
                    className: "btn-primary",
                    onClick: async () => {
                        try {
                            await deleteDepositPrice({ contractGroupId, chargeDepositId }).unwrap();
                            refetch();
                            setFormData((prev) => ({ ...prev, toastMsg: "입금 내역이 삭제되었습니다." }));
                        } catch (e) {
                            setFormData((prev) => ({ ...prev, toastMsg: e?.data?.message || "삭제 실패" }));
                        }
                        setConfirmStatus({ confirmPopupStatus: false });
                    },
                },
                {
                    label: "아니오",
                    className: "btn-cancel",
                    onClick: () => setConfirmStatus({ confirmPopupStatus: false }),
                },
            ],
        });
    };

    const handleSaveDeposit = async (values) => {
        try {
            const { inputValues, moduleStates } = values;

            // 유효성 검사
            if (!moduleStates.depositedAt) {
                setFormData((prev) => ({ ...prev, toastMsg: "입금날짜를 입력해주세요." }));
                return false;
            }
            if (!inputValues.depositPrice) {
                setFormData((prev) => ({ ...prev, toastMsg: "입금금액을 입력해주세요." }));
                return false;
            }

            const depositData = {
                depositedAt: moduleStates.depositedAt,
                depositFrom: inputValues.depositFrom || "",
                depositPrice: Number(uncomma(inputValues.depositPrice.toString())),
            };

            if (editingItem) {
                // 수정
                await putDepositPrice({
                    contractGroupId,
                    chargeDepositId: editingItem.id,
                    body: depositData,
                }).unwrap();
                setFormData((prev) => ({ ...prev, toastMsg: "입금 내역이 수정되었습니다." }));
            } else {
                // 생성
                await postDepositPrice({ contractGroupId, body: depositData }).unwrap();
                setFormData((prev) => ({ ...prev, toastMsg: "입금 내역이 추가되었습니다." }));
            }

            setIsModalOpen(false);
            setEditingItem(null);
            refetch();
        } catch (e) {
            console.log(e);
            setFormData((prev) => ({ ...prev, toastMsg: e?.message || "저장 실패" }));
            return false;
        }
    };

    // InputPopup에서 사용할 입력 필드 정의
    const getInputFields = () => [
        {
            name: "depositPrice",
            label: "입금금액",
            type: "text",
            format: "number",
            placeholder: "입금금액을 입력하세요",
            value: editingItem?.depositPrice ? comma(editingItem.depositPrice) : "",
        },
        {
            name: "depositFrom",
            label: "입금메모",
            type: "text",
            placeholder: "입금메모를 입력하세요",
            value: editingItem?.depositFrom || "",
        },
    ];

    // DatePicker 모듈 정의
    const getModules = () => {
        let dateValue = new Date(); // 기본값: 오늘 날짜

        if (editingItem) {
            // 수정 모드일 때는 해당 아이템의 날짜 사용
            if (editingItem.depositedAt) {
                dateValue = new Date(editingItem.depositedAt);
            } else if (editingItem.createdAt) {
                // depositDate가 없으면 createdAt 사용
                dateValue = new Date(editingItem.createdAt);
            }
        }

        return [
            {
                type: "datePicker",
                props: {
                    name: "depositedAt",
                    label: "입금날짜",
                    value: dateValue,
                    placeholder: "입금날짜를 선택하세요",
                },
            },
        ];
    };

    // InputPopup에서 사용할 버튼 정의
    const getButtons = () => [
        {
            label: "취소",
            className: "btn-cancel",
            onClick: () => {
                setIsModalOpen(false);
                setEditingItem(null);
            },
        },
        {
            label: editingItem ? "수정" : "추가",
            className: "btn-primary",
            closeOnClick: false,
            onClick: handleSaveDeposit,
        },
    ];

    const depositTableStyles = {
        container: {
            backgroundColor: "#fff",
            borderRadius: "4px",
            boxShadow: "0 1px 4px rgba(0, 0, 0, 0.04)",
            border: "1px solid #e8e8e8",
            maxHeight: "250px",
            overflow: "auto",
        },
        table: {
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
        },
        thead: {
            backgroundColor: "#f8f9fa",
            borderBottom: "2px solid #e9ecef",
        },
        th: {
            padding: "16px 12px",
            fontWeight: "600",
            color: "#495057",
            textAlign: "center",
            borderRight: "1px solid #e9ecef",
            fontSize: "14px",
        },
        tbody: {
            backgroundColor: "#fff",
        },
        tr: {
            borderBottom: "1px solid #e9ecef",
            transition: "background-color 0.2s ease",
        },
        td: {
            padding: "8px 4px",
            color: "#495057",
            textAlign: "center",
            borderRight: "1px solid #f1f3f4",
            verticalAlign: "middle",
        },
        emptyRow: {
            padding: "40px 12px",
            color: "#6c757d",
            textAlign: "center",
            fontSize: "14px",
            // fontStyle: "italic",
        },
        actionButton: {
            marginLeft: "6px",
            padding: "6px 12px",
            fontSize: "12px",
            borderRadius: "4px",
            border: "none",
            cursor: "pointer",
            transition: "all 0.2s ease",
            fontWeight: "500",
        },
        editButton: {
            backgroundColor: "#007bff",
            color: "#fff",
        },
        deleteButton: {
            backgroundColor: "#dc3545",
            color: "#fff",
        },
        amount: {
            fontWeight: "600",
            color: "#28a745",
        },
        date: {
            color: "#495057",
        },
    };

    return (
        <>
            <div className="item-row" data-grow="2">
                <div className="row-head">
                    <span>입금 내역</span>
                </div>
                <div
                    className="row-content"
                    style={{ display: "flex", justifyContent: "flex-start", alignItems: "flex-start", flexDirection: "column", gap: "6px" }}
                >
                    <div className="table-element" style={depositTableStyles.container}>
                        <table style={depositTableStyles.table}>
                            <colgroup>
                                <col width="20%" />
                                <col width="40%" />
                                <col width="20%" />
                                <col width="20%" />
                            </colgroup>
                            <thead style={depositTableStyles.thead}>
                                <tr>
                                    <th style={depositTableStyles.th}>입금날짜</th>
                                    <th style={depositTableStyles.th}>입금메모</th>
                                    <th style={depositTableStyles.th}>입금금액</th>
                                    <th style={{ ...depositTableStyles.th, borderRight: "none" }}>관리</th>
                                </tr>
                            </thead>
                            <tbody style={depositTableStyles.tbody}>
                                {depositListData?.data?.length > 0 ? (
                                    depositListData.data.map((item, idx) => (
                                        <tr
                                            key={item.id || idx}
                                            style={depositTableStyles.tr}
                                            onMouseEnter={(e) => (e.target.closest("tr").style.backgroundColor = "#f8f9fa")}
                                            onMouseLeave={(e) => (e.target.closest("tr").style.backgroundColor = "transparent")}
                                        >
                                            <td style={{ ...depositTableStyles.td, ...depositTableStyles.date }}>
                                                {item.depositedAt ? dateFormatDateTime(item.depositedAt, "YYYY-MM-DD") : "-"}
                                            </td>
                                            <td style={{ ...depositTableStyles.td, textAlign: "left", paddingLeft: "16px" }}>
                                                {item.depositFrom || "-"}
                                            </td>
                                            <td style={{ ...depositTableStyles.td, ...depositTableStyles.amount }}>
                                                {comma(item.depositPrice) || "0"}원
                                            </td>
                                            <td style={{ ...depositTableStyles.td, borderRight: "none" }}>
                                                <button
                                                    type="button"
                                                    style={{
                                                        ...depositTableStyles.actionButton,
                                                        ...depositTableStyles.editButton,
                                                    }}
                                                    onClick={() => handleEditDeposit(item)}
                                                    disabled={isSaving || isUpdating || isDeleting}
                                                    onMouseEnter={(e) => (e.target.style.backgroundColor = "#226BEF")}
                                                    onMouseLeave={(e) => (e.target.style.backgroundColor = "#007bff")}
                                                >
                                                    수정
                                                </button>
                                                <button
                                                    type="button"
                                                    style={{
                                                        ...depositTableStyles.actionButton,
                                                        ...depositTableStyles.deleteButton,
                                                    }}
                                                    onClick={() => handleDeleteDeposit(item.id)}
                                                    disabled={isSaving || isUpdating || isDeleting}
                                                    onMouseEnter={(e) => (e.target.style.backgroundColor = "#e66251")}
                                                    onMouseLeave={(e) => (e.target.style.backgroundColor = "#dc3545")}
                                                >
                                                    삭제
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={4} style={depositTableStyles.emptyRow}>
                                            입금 내역이 없습니다.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <button
                        type="button"
                        className="btn btn-xs btn-primary"
                        onClick={handleAddDeposit}
                        disabled={isSaving || isUpdating || isDeleting}
                    >
                        입금 내역 추가
                    </button>
                </div>
            </div>

            {isModalOpen && (
                <InputPopup
                    title={editingItem ? "입금 내역 수정" : "입금 내역 추가"}
                    description="입금 내역 정보를 입력해주세요."
                    width="480px"
                    inputs={getInputFields()}
                    modules={getModules()}
                    buttons={getButtons()}
                    onClose={() => {
                        setIsModalOpen(false);
                        setEditingItem(null);
                    }}
                />
            )}

            {/* Confirm 팝업 */}
            {confirmStatus?.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() => setConfirmStatus({ confirmPopupStatus: false })}
                    title={confirmStatus.confirmTitle}
                    description={confirmStatus.confirmDescription}
                    buttons={confirmStatus.confirmButtons}
                />
            )}
        </>
    );
};
